import React, { useEffect, useRef, useState } from 'react';
import { Card, Spin, Alert } from 'antd';
import { googleMapsService } from '../../services/googleMaps';
import type { ApartmentFromPlaces } from '../../types/apartments';

interface GoogleMapsViewProps {
  apartments: ApartmentFromPlaces[];
  userLocation: { lat: number; lng: number } | null;
  searchLocation: { lat: number; lng: number } | null;
  onApartmentClick: (apartment: ApartmentFromPlaces) => void;
  onLocationChange: (newLocation: { lat: number; lng: number }) => void;
  height?: number;
}

const GoogleMapsView: React.FC<GoogleMapsViewProps> = ({
  apartments,
  userLocation,
  searchLocation,
  onApartmentClick,
  onLocationChange,
  height = 400,
}) => {

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<any[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);

  // Initialize Google Maps
  useEffect(() => {
    const initGoogleMaps = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Initialize Google Maps using the centralized service
        await googleMapsService.initialize();

        setIsMapReady(true);
        setIsLoading(false);
      } catch (err) {
        console.error('[Map] Error loading Google Maps:', err);
        setError('Failed to load Google Maps');
        setIsLoading(false);
      }
    };

    initGoogleMaps();
  }, []);

  // Create map when container is ready
  useEffect(() => {
    const createMap = async () => {
      if (!isMapReady || !mapRef.current) return;

      try {
        const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        
        const center = searchLocation || userLocation || { lat: 40.7128, lng: -74.0060 };
        
        const map = new Map(mapRef.current, {
          center,
          zoom: 12,
          mapId: 'DEMO_MAP_ID',
        });

        mapInstanceRef.current = map;
      } catch (error) {
        console.error('[Map] Error creating map:', error);
        setError('Failed to create map');
      }
    };

    createMap();
  }, [isMapReady, userLocation, searchLocation]);

  // Add markers when apartments change
  useEffect(() => {
    const addMarkers = async () => {
      if (!mapInstanceRef.current) {
        return;
      }

      if (!apartments.length) {
        return;
      }

      try {
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
        const { LatLngBounds } = await google.maps.importLibrary("core") as google.maps.CoreLibrary;
        
        const map = mapInstanceRef.current;
        const bounds = new LatLngBounds();

        // Clear existing markers
        markersRef.current.forEach(marker => marker.map = null);
        markersRef.current = [];

        // Add search location marker (draggable) - RED
        if (searchLocation) {
          const searchLatLng = new google.maps.LatLng(searchLocation.lat, searchLocation.lng);
          const searchMarker = new AdvancedMarkerElement({
            map,
            position: searchLatLng,
            title: 'Search Location (Drag to move)',
            content: new google.maps.marker.PinElement({
              background: '#EA4335',
              borderColor: '#ffffff',
              scale: 1.3, // Make it larger
            }).element,
          });
          
          // Add pulsing animation to search marker
          const searchElement = searchMarker.content as HTMLElement;
          if (searchElement) {
            searchElement.style.animation = 'pulse 2s infinite';
          }
          
          // Make the marker draggable
          searchMarker.gmpDraggable = true;
          
          searchMarker.addListener('dragend', () => {
            const newPosition = searchMarker.position as any;
            onLocationChange({
              lat: newPosition.lat || newPosition.lat(),
              lng: newPosition.lng || newPosition.lng(),
            });
          });
          
          markersRef.current.push(searchMarker);
          bounds.extend(searchLatLng);
        }

        // Add user location marker if different from search location - BLUE
        if (userLocation && (!searchLocation || 
            (userLocation.lat !== searchLocation.lat || userLocation.lng !== searchLocation.lng))) {
          const userLatLng = new google.maps.LatLng(userLocation.lat, userLocation.lng);
          const userMarker = new AdvancedMarkerElement({
            map,
            position: userLatLng,
            title: 'Your Current Location',
            content: new google.maps.marker.PinElement({
              background: '#4285F4', // Blue color
              borderColor: '#ffffff',
              scale: 1.5, // Make it larger
            }).element,
          });
          
          // Set higher z-index to keep it on top
          userMarker.zIndex = 1000;
          
          // Add bouncing animation to user location marker
          const userElement = userMarker.content as HTMLElement;
          if (userElement) {
            userElement.style.animation = 'bounce 1.5s infinite';
          }
          
          markersRef.current.push(userMarker);
          bounds.extend(userLatLng);
        }

        // Add apartment markers
        apartments.forEach((apartment) => {
          // Skip apartments with invalid coordinates
          if (isNaN(apartment.latitude) || isNaN(apartment.longitude)) {
            return;
          }

          const position = new google.maps.LatLng(apartment.latitude, apartment.longitude);
          
          const marker = new AdvancedMarkerElement({
            map,
            position,
            title: apartment.name,
            content: new google.maps.marker.PinElement({
              background: '#666666',
              borderColor: '#ffffff',
              scale: 0.8, // Make apartment markers smaller
            }).element,
          });

          // Add click listener
          marker.addListener('click', () => {
            map.panTo(position);
            onApartmentClick(apartment);
          });

          markersRef.current.push(marker);
          bounds.extend(position);
        });

        // Fit bounds to show all markers
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      } catch (error) {
        console.error('[Map] Error adding markers:', error);
      }
    };

    addMarkers();
  }, [apartments, userLocation, searchLocation, onApartmentClick, onLocationChange]);

  if (error) {
    return (
      <Card title="Map View" style={{ height }}>
        <Alert
          message="Failed to load Google Maps"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Map View" style={{ height }}>
        <div style={{ 
          height: height - 80, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card title="Map View" style={{ height }}>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0) scale(1); }
            40% { transform: translateY(-15px) scale(1.1); }
            60% { transform: translateY(-8px) scale(1.05); }
          }
          

        `}
      </style>
      <div
        ref={mapRef}
        style={{ 
          height: height - 80, 
          width: '100%',
          borderRadius: 8,
          border: '1px solid #d9d9d9',
          backgroundColor: '#f5f5f5',
          minHeight: '300px'
        }}
      />
    </Card>
  );
};

export default GoogleMapsView; 